import { 
  reactive,
  computed, 
  useContext 
} from '@nuxtjs/composition-api';
import type {
  UseCompareInterface,
} from './useCompare';
import { useCompareStore } from '~/modules/catalog/category/stores/compare';
import { map } from 'lodash-es';
import {
  useUiNotification,
} from '~/composables';
import useCompareApi from './commands/useCompareApi';

const compareState = reactive({
  showPanel: false,
});

export function useCompare(): UseCompareInterface {

  const maxSize = 4;
  const compareStore = useCompareStore();
  const compares = computed(() => compareStore.compares);
  const { send: sendNotification } = useUiNotification();
  const { app } = useContext();
  const { state } = app.context.$vsf.$magento.config;
  const { 
    createCompareList,
    compareList,
    addProductsToCompareList,
    removeProductsFromCompareList,
    assignCompareListToCustomer,
    deleteCompareList,
  } = useCompareApi();

  const isInCompare = (productId): boolean => {
    return !!compares.value?.find((item) => item.product.id === productId);
  };

  const openPanel = () => {
    compareState.showPanel = true;
  };

  const closePanel = () => {
    compareState.showPanel = false;
  };

  const showError = (message: string) => {
    sendNotification({
      id: Symbol('compare'),
      message: message,
      type: 'danger',
      icon: 'error',
      persist: false,
      title: 'Compare',
    });
  };

  const createCompare = async (): Promise<void> => {
    const response = await createCompareList({products:[]});
    if (response.errors || !response.data?.['createCompareList']?.['uid']) {
      const errorMessage = response.errors?.[0].message ?? '[create compare]request exception';
      throw new Error(String(errorMessage));
    }
    const uid = response.data?.['createCompareList']['uid'];
    state.setCompareId(uid);
  };

  const loadCompare = async (reset = false): Promise<void> => {
    try {
      if (reset) {
        await assignCompareListToCustomer({"uid": state.getCompareId()});
        await deleteCompareList({"uid": state.getCompareId()});
        await state.removeCompareId();
      }
      if (!state.getCompareId()) {
        await createCompare();
      }
      const response = await compareList({"uid": state.getCompareId()});
      if (response.errors || !response.data?.['compareList']?.['items']) {
        const errorMessage = response.errors?.[0].message ?? '[compare list]request exception';
        throw new Error(String(errorMessage));
      }
      compareStore.$patch((state) => {
        state.compares = response.data?.['compareList']?.['items']??[];
      });
    } catch (error) {
      showError(error.message);
      await state.removeCompareId();
    }
  };

  const addCompare = async (productId) => {
    try {
      if (compares.value.length >= maxSize) {
        throw new Error(String(app.i18n.t('Exceeded the maximum quantity')));
      }
      const response = await addProductsToCompareList({"uid": state.getCompareId(), products: [productId]});
      if (response.errors || !response.data?.['addProductsToCompareList']?.['items']) {
        const errorMessage = response.errors?.[0].message ?? '[add compare]request exception';
        throw new Error(String(errorMessage));
      }
      compareStore.$patch((state) => {
        state.compares = response.data?.['addProductsToCompareList']?.['items']??[];
      });
    } catch (error) {
      showError(error.message);
    }
  };

  const removeCompare = async (productId) => {
    try {
      const response = await removeProductsFromCompareList({"uid": state.getCompareId(), products: [productId]});
      if (response.errors || !response.data?.['removeProductsFromCompareList']?.['items']) {
        const errorMessage = response.errors?.[0].message ?? '[remove compare]request exception';
        throw new Error(String(errorMessage));
      }
      compareStore.$patch((state) => {
        state.compares = response.data?.['removeProductsFromCompareList']?.['items']??[];
      });
    } catch (error) {
      showError(error.message);
    }
  };

  const removeAllCompare = async () => {
    try {
      const productIds = map(compares.value, (item) => item.product.id);
      const response = await removeProductsFromCompareList({"uid": state.getCompareId(), products: productIds});
      if (response.errors || !response.data?.['removeProductsFromCompareList']?.['items']) {
        const errorMessage = response.errors?.[0].message ?? '[remove compare all]request exception';
        throw new Error(String(errorMessage));
      }
      compareStore.$patch((state) => {
        state.compares = response.data?.['removeProductsFromCompareList']?.['items']??[];
      });
    } catch (error) {
      showError(error.message);
    }
  };

  const deleteCompare = async () => {
    try {
      await deleteCompareList({"uid": state.getCompareId()});
      compareStore.$patch((state) => {
        state.compares = [];
      });
    } catch (error) {
      showError(error.message);
    }
  };

  return {
    showPanel: computed(() => compareState.showPanel),
    compares,
    openPanel,
    closePanel,
    isInCompare,
    loadCompare,
    addCompare,
    removeCompare,
    removeAllCompare,
    deleteCompare,
  };
};

export * from './useCompare';
export default useCompare;
